<template>
  <v-row no-gutters>
    <v-col class="d-flex justify-start font-weight-thin text--disabled caption">
      <v-fade-transition v-if="lastCalculationDate">
        <span class="pt-3 pb-1 ml-9">
          {{ $t('currentPostedPricesChart.lastCalcDate') }}:
          {{ lastCalculationDate | formatDate('DD.MM.YYYY - HH:mm') }}
        </span>
      </v-fade-transition>
      <v-skeleton-loader
        v-show="!lastCalculationDate"
        width="15rem"
        light
        class="p-0 m-0 pl-9"
        height="2rem"
        type="text"
      />
    </v-col>
    <chartjs-wrapper
      ref="chartJsWrapper"
      class="white float-right"
      :chartId="chartId"
      :chart-data="currentPostedPricesChartData"
      :type="'line'"
      :options="currentPostedPricesChartOptions"
      :chart-height="20"
      :chart-width="'100%'"
      hover-line
      @selected="clickedChartPoint"
    >
    </chartjs-wrapper>
  </v-row>
</template>

<script>
import ChartjsWrapper from './ChartjsWrapper.vue'
import definitions from '../../../definitions'
import DateRange from '@/classes-shared/dates/DateRange'
import _ from 'lodash'
import moment from 'moment'
import convertHex from '@/classes-internal/utils/convertHex'

export default {
  name: 'CurrentPostedPrices',

  components: {
    ChartjsWrapper,
  },

  props: {
    dateRange: {
      type: Object,
      required: true,
      validator: (value) => {
        return value instanceof DateRange
      },
    },
    selectedDefinitions: {
      required: true,
      type: Array,
    },
    currentPostedPrices: {
      required: true,
      type: Array,
    },
    selectedDate: {
      required: true,
      type: Date,
    },
    chartId: {
      required: true,
      type: String,
    },
    disableXAxis: {
      required: false,
      type: Boolean,
      default: false,
    },
    maxDisplacedItems: {
      type: Number,
      default: definitions.colors.lineChart.length,
    },
  },

  computed: {
    lastCalculationDate() {
      if (this.currentPostedPrices.length > 0) {
        return this.currentPostedPrices[0].lastCalculationDate
      }

      return false
    },
    currentPostedPricesChartData() {
      const linesData = _.groupBy(
        this.currentPostedPrices,
        'productDefinitionId'
      )
      const data = {
        labels: [],
        datasets: Object.keys(linesData).map((productDefinitionId) => {
          const products = linesData[productDefinitionId]
          const selection = _.find(
            this.selectedDefinitions,
            function (selection) {
              return selection.item.id === parseInt(productDefinitionId)
            }
          )

          const backgroundColor = convertHex(selection?.color, 10)

          return {
            borderColor: selection.color,
            borderWidth: 1.5,
            backgroundColor,
            fill: '+1',
            pointBorderWidth: 0,
            hoverPointBorderWidth: 2,
            pointBorderColor: products.map((postedPrice) =>
              postedPrice.isCustomPrice()
                ? 'rgba(231, 76, 60,1.0)'
                : selection.color
            ),
            pointBackgroundColor: products.map((postedPrice) =>
              postedPrice.isCustomPrice() ? '#c0392b' : selection.color
            ),
            data: products.map((postedPrice) => ({
              x: postedPrice.getValidAt(),
              y: postedPrice.getChartFormattedPrice(),
            })),
            label: selection?.item?.getTranslatedName(),
          }
        }),
      }

      return data
    },
    currentPostedPricesChartOptions() {
      const defaultOptions = {
        responsive: true,
        layout: {
          padding: {
            right: 60,
            bottom: 50,
          },
        },
        maintainAspectRatio: false,
        annotation: {
          annotations: [
            {
              drawTime: 'afterDatasetsDraw',
              id: 'todayLine',
              type: 'line',
              mode: 'vertical',
              scaleID: 'x-axis-0',
              value: new Date(),
              borderColor: definitions.colors.named.value,
              borderWidth: 1,
              borderDash: [5, 5],
              borderDashOffset: 0,
              label: {
                backgroundColor: definitions.colors.named.value,
                content: i18n.t('general.today'),
                enabled: true,
                fontSize: 10,
                position: 'top',
              },
            },
            {
              drawTime: 'afterDatasetsDraw',
              id: 'selectionLine',
              type: 'line',
              mode: 'vertical',
              scaleID: 'x-axis-0',
              value: this.selectedDate,
              borderColor: definitions.colors.named.seasonality,
              borderWidth: 1,
              borderDash: [5, 5],
              borderDashOffset: 0,
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItems, data) => {
              return (
                data.datasets[tooltipItems.datasetIndex].label +
                ': ' +
                tooltipItems.yLabel +
                ` ${store.getters
                  .getCurrentDestinationInstance()
                  .getCurrency()}`
              )
            },
          },
          mode: 'point',
          intersect: true,
          backgroundColor: 'rgba(255,255,255,0.9)',
          titleFontColor: 'rgba(140, 140, 140, 1)',
          bodyFontColor: 'black',
          borderColor: 'rgba(160, 160, 160, 1)',
          borderWidth: 1,
          xPadding: 10,
          yPadding: 10,
          titleMarginBottom: 12,
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString:
                  this.$t('currentPostedPricesChart.ylabel') +
                  ` (${store.getters
                    .getCurrentDestinationInstance()
                    .getCurrency()})`,
              },
              gridLines: {
                color: '#EEE',
              },
              afterFit: function (scale) {
                scale.width = 60 // offset to align charts
              },
            },
          ],
          xAxes: [
            {
              type: 'time',
              time: {
                unit: this?.unit,
                displayFormats: {
                  day: 'D. MMM',
                  week: 'll',
                  month: 'MMM YYYY',
                },
                tooltipFormat: 'ddd DD.MM.YYYY',
                stepSize: 1,
                min: this?.dateRange?.startDate.getTime(),
                max: this?.dateRange?.endDate.getTime(),
              },
              ticks: {
                display: true,
              },
              gridLines: {
                display: false,
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        legendCallback: (chart) => {
          const renderLabels = (chart) => {
            const { data } = chart
            return data.datasets
              .map(
                (dataset, i) => `
              <li>
                    <div id="legend-${i}-item" class="legend-item">
                      <span class="legend-marker" style="background-color:
                        ${dataset.borderColor}">
                        &nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                      ${
                        dataset.label &&
                        `<span class="label">${dataset.label}</span>`
                      }
                    </div>
                </li>
            `
              )
              .join('')
          }
          return `
          <ul class="chartjs-legend">
            ${renderLabels(chart)}
          </ul>`
        },
      }

      if (this.disableXAxis) {
        defaultOptions.scales.xAxes[0].ticks.fontColor = 'rgba(0,0,0,0)'
      }
      return defaultOptions
    },
    unit() {
      const diff = moment(this.dateRange.endDate).diff(
        moment(this.dateRange.startDate),
        'd'
      )
      return 'day'
    },
  },

  methods: {
    clickedChartPoint(event) {
      if (event.activePoint) {
        const datasetIndex = event.activePoint['_datasetIndex']
        const chartData = event.activePoint['_chart'].config.data
        const idx = event.activePoint['_index']
        this.$emit('currentDate', selectedDate)
        const selectedDate = chartData.datasets[datasetIndex].data[idx]['x']
        if (selectedDate) {
          this.$store.commit('OverviewPricing/setSelectedDate', selectedDate)
        }
      }
    },
  },
}
</script>
